import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import brandAPI from "../../../../services/brandapi";

const BrandCreateForm = ({ show, onHide, editingId, onSuccess }) => {
    const [brandName, setBrandName] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [status, setStatus] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const isEditMode = Boolean(editingId);

    useEffect(() => {
        if (isEditMode && show) {
            const fetchBrand = async () => {
                setLoading(true);
                try {
                    const res = await brandAPI.getBrandById(editingId);
                    const data = res.data.brand;
                    setBrandName(data.name || "");
                    setStatus(data.status === 1);
                    if (data.image) {
                        setImagePreview(`${process.env.REACT_APP_IMAGE_URL}/uploads/${data.image}`);
                    }
                } catch (err) {
                    console.error("Failed to fetch brand:", err);
                } finally {
                    setLoading(false);
                }
            };
            fetchBrand();
        } else {
            resetForm();
        }
    }, [editingId, show]);

    const resetForm = () => {
        setBrandName("");
        setImageFile(null);
        setImagePreview("");
        setStatus(true);
        setErrors({});
    };

    const validateForm = () => {
        const newErrors = {};
        if (!brandName.trim()) newErrors.brandName = "Brand name is required";
        return newErrors;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("name", brandName);
            formData.append("status", status ? 1 : 0);
            if (imageFile) formData.append("brand_image", imageFile);
            if (isEditMode) formData.append("id", editingId);

            if (isEditMode) {
                await brandAPI.updateBrand(formData);
            } else {
                await brandAPI.createBrand(formData);
            }

            onSuccess();
            onHide();
            resetForm();
        } catch (err) {
            console.error("Error saving brand:", err);
            setErrors({ common: "Something went wrong. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="catalog-modal">
            <Modal.Header closeButton>
                <Modal.Title>{isEditMode ? "Edit Brand" : "Create Brand"}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {errors.common && (
                        <div className="text-danger mb-2">{errors.common}</div>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Brand Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={brandName}
                            onChange={(e) => setBrandName(e.target.value)}
                            isInvalid={!!errors.brandName}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.brandName}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Brand Image</Form.Label>
                        <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
                        {imagePreview && (
                            <div className="mt-2">
                                <img
                                    src={imagePreview}
                                    alt="Brand Preview"
                                    style={{ maxWidth: "100%", height: "auto" }}
                                />
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="brand-status"
                            label={status ? "Active" : "Inactive"}
                            checked={status}
                            onChange={(e) => setStatus(e.target.checked)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : isEditMode ? "Update" : "Create"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default BrandCreateForm;
